import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  Leaf, 
  TrendingUp, 
  Coffee,
  Recycle,
  CloudRain,
  Sun,
  Wind,
  Calendar,
  Droplets,
  DollarSign,
  ArrowRight,
  Globe,
  Package,
  BarChart3,
  Loader,
  Check,
  AlertTriangle,
  Map,
  Sprout,
  Zap,
  ShoppingBag
} from 'lucide-react';
import { motion } from 'framer-motion';

// Charts
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

// Components
import CropAnalytics from '../components/UserDashboard/CropAnalytics';
import FarmMetrics from '../components/UserDashboard/FarmMetrics';
import CommunityFeed from '../components/UserDashboard/CommunityFeed';
import MarketPrices from '../components/Dashboard/MarketPrices';
import CoffeeByproducts from '../components/Dashboard/CoffeeByproducts';
import MicroForecast from '../components/Dashboard/MicroForecast';
import CommunityMarketplace from '../components/Dashboard/CommunityMarketplace';
import AnimatedSection from '../components/AnimatedSection';
import { Card, Badge, Button } from '../components/ui';

// Mock Data
const farmOverviewData = {
  farmSize: '12 acres',
  location: 'Jinja, Uganda',
  farmType: 'Regenerative Agroforestry',
  elevation: '1,200 meters',
  annualRainfall: '1,250 mm',
  farmers: 273,
  certifications: ['Organic', 'Rainforest Alliance', 'Fair Trade']
};

const cropData = [
  { name: 'Coffee', area: 7, yield: '3.5 tons/hectare', revenue: '$28,000' },
  { name: 'Banana', area: 2, yield: '18 tons/hectare', revenue: '$12,000' },
  { name: 'Cassava', area: 1.5, yield: '25 tons/hectare', revenue: '$8,500' },
  { name: 'Beans', area: 1, yield: '1.8 tons/hectare', revenue: '$5,500' },
  { name: 'Maize', area: 0.5, yield: '3.2 tons/hectare', revenue: '$2,800' },
];

const monthlyProduction = [
  { month: 'Jan', coffee: 1.2, banana: 1.5, cassava: 2.1 },
  { month: 'Feb', coffee: 0.9, banana: 1.6, cassava: 2.0 },
  { month: 'Mar', coffee: 0.7, banana: 1.8, cassava: 1.9 },
  { month: 'Apr', coffee: 0.6, banana: 1.7, cassava: 1.8 },
  { month: 'May', coffee: 0.8, banana: 1.5, cassava: 1.7 },
  { month: 'Jun', coffee: 1.5, banana: 1.4, cassava: 1.6 },
  { month: 'Jul', coffee: 2.3, banana: 1.5, cassava: 1.8 },
  { month: 'Aug', coffee: 2.8, banana: 1.6, cassava: 2.0 },
  { month: 'Sep', coffee: 3.2, banana: 1.7, cassava: 2.1 },
  { month: 'Oct', coffee: 2.9, banana: 1.8, cassava: 2.2 },
  { month: 'Nov', coffee: 2.4, banana: 1.7, cassava: 2.0 },
  { month: 'Dec', coffee: 1.8, banana: 1.6, cassava: 1.9 },
];

const revenueData = [
  { month: 'Jan', revenue: 5200, expenses: 3100, profit: 2100 },
  { month: 'Feb', revenue: 4800, expenses: 3000, profit: 1800 },
  { month: 'Mar', revenue: 4500, expenses: 2900, profit: 1600 },
  { month: 'Apr', revenue: 4200, expenses: 2800, profit: 1400 },
  { month: 'May', revenue: 4800, expenses: 2900, profit: 1900 },
  { month: 'Jun', revenue: 6000, expenses: 3200, profit: 2800 },
  { month: 'Jul', revenue: 7800, expenses: 3800, profit: 4000 },
  { month: 'Aug', revenue: 8500, expenses: 4000, profit: 4500 },
  { month: 'Sep', revenue: 9200, expenses: 4200, profit: 5000 },
  { month: 'Oct', revenue: 8800, expenses: 4100, profit: 4700 },
  { month: 'Nov', revenue: 7500, expenses: 3800, profit: 3700 },
  { month: 'Dec', revenue: 6200, expenses: 3500, profit: 2700 },
];

const farmImpactData = [
  { name: 'Carbon Sequestered', value: 125, unit: 'tons', change: '+15%' },
  { name: 'Water Saved', value: 3.5, unit: 'million liters', change: '+30%' },
  { name: 'Biodiversity Increase', value: 40, unit: '%', change: '+8%' },
  { name: 'Farmer Income Increase', value: 45, unit: '%', change: '+5%' },
];

const valueAddedProducts = [
  { 
    name: 'Coffee Cherry Syrup',
    production: '850 liters',
    revenue: '$12,500',
    description: 'Natural sweetener produced from coffee cherry pulp, reducing waste and creating additional revenue',
    image: 'https://images.unsplash.com/photo-1581996323777-9fde24488c9b'
  },
  { 
    name: 'Coffee Flour',
    production: '1,200 kg',
    revenue: '$9,800',
    description: 'Gluten-free flour made from dried coffee cherry pulp, high in fiber and antioxidants',
    image: 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35'
  },
  { 
    name: 'Biochar',
    production: '2,500 kg',
    revenue: '$6,250',
    description: 'Carbon-rich soil amendment created from agricultural waste, improving soil health and sequestering carbon',
    image: 'https://images.unsplash.com/photo-1523049673857-eb18f1d7b578'
  },
];

// Seasonal calendar data
const seasonalCalendar = {
  coffee: {
    planting: [2, 3, 9, 10], // Mar, Apr, Oct, Nov
    maintenance: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], // All year
    harvest: [5, 6, 7, 8, 9] // Jun, Jul, Aug, Sep, Oct
  },
  banana: {
    planting: [2, 3, 4, 9, 10], // Mar, Apr, May, Oct, Nov
    maintenance: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], // All year
    harvest: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] // All year
  },
  maize: {
    planting: [2, 3, 8, 9], // Mar, Apr, Sep, Oct
    maintenance: [2, 3, 4, 5, 8, 9, 10, 11], // Mar, Apr, May, Jun, Sep, Oct, Nov, Dec
    harvest: [6, 7, 0, 1] // Jul, Aug, Jan, Feb
  },
  beans: {
    planting: [2, 3, 8, 9], // Mar, Apr, Sep, Oct
    maintenance: [2, 3, 4, 8, 9, 10], // Mar, Apr, May, Sep, Oct, Nov
    harvest: [5, 6, 11, 0] // Jun, Jul, Dec, Jan
  },
  cassava: {
    planting: [2, 3, 4], // Mar, Apr, May
    maintenance: [2, 3, 4, 5, 6, 7, 8, 9, 10], // Mar through Nov
    harvest: [10, 11, 0, 1] // Nov, Dec, Jan, Feb
  }
};

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const farmHubMembers = [
  {
    name: 'Edward',
    role: 'Hub Manager',
    years: 5,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d'
  },
  {
    name: 'Sarah',
    role: 'Processing Lead',
    years: 3,
    image: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce'
  },
  {
    name: 'David',
    role: 'Agroforestry Specialist',
    years: 4,
    image: 'https://images.unsplash.com/photo-1507101105822-7472b28e22ac'
  },
  {
    name: 'Maria',
    role: 'Community Coordinator',
    years: 2,
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956'
  },
];

const revenueDistribution = [
  { name: 'Farmers', value: 70 },
  { name: 'Operations', value: 20 },
  { name: 'Training', value: 5 },
  { name: 'Cooperative', value: 5 }
];

const COLORS = ['#16a34a', '#9333ea', '#3b82f6', '#f59e0b'];

const EdwardsHamletDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProduct, setSelectedProduct] = useState(valueAddedProducts[0]);
  
  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1200);
    
    return () => clearTimeout(timer);
  }, []);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader className="h-12 w-12 text-avn-purple-600 animate-spin" />
          <p className="text-avn-purple-600 text-lg">Loading Edward's Hamlet Dashboard...</p>
        </div>
      </div>
    );
  }

  const renderOverviewTab = () => (
    <div className="space-y-8">
      {/* Farm Overview Card */}
      <Card className="bg-white p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-avn-purple-100 p-4 rounded-lg">
              <Building2 className="h-8 w-8 text-avn-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Edward's Hamlet Regeneration Centre</h2>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="h-4 w-4" />
                <span>{farmOverviewData.location}</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {farmOverviewData.certifications.map((cert, index) => (
              <Badge 
                key={index}
                variant={index === 0 ? 'green' : index === 1 ? 'blue' : 'amber'}
                icon={<Check className="h-3 w-3" />}
              >
                {cert}
              </Badge>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Sprout className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Farm Size</span>
            </div>
            <div className="text-2xl font-bold">{farmOverviewData.farmSize}</div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Leaf className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Farm Type</span>
            </div>
            <div className="text-2xl font-bold">Regenerative</div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Users className="h-4 w-4 text-avn-purple-600" />
              <span className="text-sm font-medium">Community Size</span>
            </div>
            <div className="text-2xl font-bold">{farmOverviewData.farmers}</div>
            <div className="text-sm text-gray-600">Farmers</div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <CloudRain className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">Annual Rainfall</span>
            </div>
            <div className="text-2xl font-bold">{farmOverviewData.annualRainfall}</div>
          </div>
        </div>
      </Card>
      
      {/* Farm Metrics and Weather */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Card className="h-full">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-avn-purple-600" />
                <h3 className="text-xl font-bold">Production Overview</h3>
              </div>
              <Badge variant="purple">Monthly Data</Badge>
            </div>
            
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyProduction}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="coffee" 
                    name="Coffee (tons)" 
                    stroke="#6d28d9" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6, stroke: '#4c1d95' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="banana" 
                    name="Banana (tons)" 
                    stroke="#16a34a" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6, stroke: '#14532d' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cassava" 
                    name="Cassava (tons)" 
                    stroke="#f59e0b" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6, stroke: '#b45309' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
        
        <div>
          <MicroForecast farmLocation="Jinja, Uganda" />
        </div>
      </div>
      
      {/* Crop Distribution and Impact */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="h-full">
          <div className="flex items-center gap-3 mb-6">
            <Leaf className="h-6 w-6 text-green-600" />
            <h3 className="text-xl font-bold">Crop Distribution</h3>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={cropData.map(crop => ({ name: crop.name, value: crop.area }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {cropData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value} acres`, name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-4">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500">
                <tr>
                  <th className="pb-2">Crop</th>
                  <th className="pb-2">Area</th>
                  <th className="pb-2">Yield</th>
                  <th className="pb-2">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cropData.map((crop, index) => (
                  <tr key={index}>
                    <td className="py-2">{crop.name}</td>
                    <td className="py-2">{crop.area} acres</td>
                    <td className="py-2">{crop.yield}</td>
                    <td className="py-2">{crop.revenue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        
        <Card className="h-full">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="h-6 w-6 text-avn-purple-600" />
            <h3 className="text-xl font-bold">Regenerative Impact</h3>
          </div>
          
          <div className="space-y-6">
            {farmImpactData.map((impact, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium">{impact.name}</div>
                  <div className="flex items-center gap-1 text-green-600 text-sm">
                    <TrendingUp className="h-4 w-4" />
                    <span>{impact.change}</span>
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <div className="text-3xl font-bold">
                    {impact.value}
                    <span className="text-sm font-normal text-gray-500 ml-1">{impact.unit}</span>
                  </div>
                  <div className="w-32 h-10">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={[
                        {month: 'Jan', value: impact.value * 0.7},
                        {month: 'Feb', value: impact.value * 0.8},
                        {month: 'Mar', value: impact.value * 0.85},
                        {month: 'Apr', value: impact.value * 0.9},
                        {month: 'May', value: impact.value * 0.95},
                        {month: 'Jun', value: impact.value}
                      ]}>
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke={
                            index === 0 ? "#16a34a" : 
                            index === 1 ? "#3b82f6" : 
                            index === 2 ? "#8b5cf6" : 
                            "#f59e0b"
                          } 
                          strokeWidth={2} 
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
  
  const renderFinancialTab = () => (
    <div className="space-y-8">
      {/* Financial Overview */}
      <Card className="bg-white p-6">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="h-6 w-6 text-green-600" />
          <h3 className="text-xl font-bold">Financial Performance</h3>
        </div>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip 
                formatter={(value) => [`$${value.toLocaleString()}`, undefined]}
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Legend />
              <Bar 
                dataKey="revenue" 
                name="Revenue" 
                fill="#9333ea"
              />
              <Bar 
                dataKey="expenses" 
                name="Expenses" 
                fill="#6b21a8"
              />
              <Bar 
                dataKey="profit" 
                name="Profit" 
                fill="#16a34a"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      
      {/* Revenue Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="col-span-1">
          <div className="flex items-center gap-3 mb-6">
            <DollarSign className="h-6 w-6 text-avn-purple-600" />
            <h3 className="text-xl font-bold">Revenue Distribution</h3>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revenueDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {revenueDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, undefined]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-4">
            <h4 className="font-medium mb-3">AVN's Ethical Profit-Sharing Model</h4>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                <span className="text-sm">70% to farmers (vs. 6% in traditional models)</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-3 h-3 bg-avn-purple-600 rounded-full"></div>
                <span className="text-sm">20% to operations and platform development</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                <span className="text-sm">5% to training and capacity building</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-3 h-3 bg-amber-600 rounded-full"></div>
                <span className="text-sm">5% to cooperative maintenance</span>
              </li>
            </ul>
          </div>
        </Card>
        
        <Card className="col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <Package className="h-6 w-6 text-avn-purple-600" />
            <h3 className="text-xl font-bold">Value-Added Products</h3>
          </div>
          
          <div className="space-y-6">
            <div className="flex overflow-x-auto pb-2 gap-4">
              {valueAddedProducts.map((product, index) => (
                <div 
                  key={index}
                  onClick={() => setSelectedProduct(product)}
                  className={`flex-shrink-0 w-48 rounded-xl overflow-hidden cursor-pointer transition-all ${
                    selectedProduct.name === product.name ? 'ring-2 ring-avn-purple-600 shadow-lg' : 'ring-1 ring-gray-200'
                  }`}
                >
                  <div className="h-24 bg-gray-100">
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="font-medium text-sm mb-1 truncate">{product.name}</h4>
                    <div className="text-xs text-gray-500">{product.revenue}</div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="md:w-1/3 h-40 rounded-lg overflow-hidden bg-gray-200">
                  <img 
                    src={selectedProduct.image} 
                    alt={selectedProduct.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:w-2/3">
                  <h3 className="text-lg font-bold mb-2">{selectedProduct.name}</h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <div className="text-sm text-gray-500">Annual Production</div>
                      <div className="font-medium">{selectedProduct.production}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Annual Revenue</div>
                      <div className="font-medium">{selectedProduct.revenue}</div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-sm">{selectedProduct.description}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Payment Information */}
      <Card className="bg-white p-6">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="h-6 w-6 text-green-600" />
          <h3 className="text-xl font-bold">Payment Structure</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h4 className="font-medium mb-4">Payment Timeline</h4>
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 p-2 rounded-full mt-1">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h5 className="font-medium">Immediate 15% Payment</h5>
                    <p className="text-gray-600 text-sm">
                      Farmers receive 15% of their 70% profit share immediately upon delivery of produce to Edward's Hamlet.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 p-2 rounded-full mt-1">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h5 className="font-medium">Remaining 85% Within 30 Days</h5>
                    <p className="text-gray-600 text-sm">
                      The remaining 85% of the farmer's 70% profit share is paid within 30 days of delivery, after quality assurance and buyer settlement.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-4">Payment Statistics (Last Quarter)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Average Payment</div>
                <div className="text-2xl font-bold">$845</div>
                <div className="text-xs text-gray-500">Per Farmer</div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Total Payments</div>
                <div className="text-2xl font-bold">$230,685</div>
                <div className="text-xs text-gray-500">To 273 Farmers</div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Payment Speed</div>
                <div className="text-2xl font-bold">24 Days</div>
                <div className="text-xs text-gray-500">Average Final Payment</div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Income Increase</div>
                <div className="text-2xl font-bold">45%</div>
                <div className="text-xs text-gray-500">Year-over-Year</div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
  
  const renderFarmingTab = () => (
    <div className="space-y-8">
      {/* Seasonal Calendar */}
      <Card className="bg-white p-6">
        <div className="flex items-center gap-3 mb-6">
          <Calendar className="h-6 w-6 text-avn-purple-600" />
          <h3 className="text-xl font-bold">Seasonal Farming Calendar</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left py-2 px-3 bg-gray-50 sticky left-0 z-10">Crop</th>
                {months.map(month => (
                  <th key={month} className="text-center py-2 px-2">{month}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(seasonalCalendar).map(([crop, seasons]) => (
                <React.Fragment key={crop}>
                  <tr>
                    <td className="font-medium py-2 px-3 bg-gray-50 sticky left-0 z-10 capitalize">{crop}</td>
                    {months.map((month, monthIndex) => {
                      const isPlanting = seasons.planting.includes(monthIndex);
                      const isHarvest = seasons.harvest.includes(monthIndex);
                      
                      return (
                        <td key={month} className="px-2 py-3 text-center">
                          {isPlanting && isHarvest ? (
                            <div className="w-6 h-6 rounded-full bg-purple-500 mx-auto" title="Planting & Harvest Overlap"></div>
                          ) : isPlanting ? (
                            <div className="w-6 h-6 rounded-full bg-green-200 border-2 border-green-500 mx-auto" title="Planting"></div>
                          ) : isHarvest ? (
                            <div className="w-6 h-6 rounded-full bg-amber-200 border-2 border-amber-500 mx-auto" title="Harvest"></div>
                          ) : seasons.maintenance.includes(monthIndex) ? (
                            <div className="w-6 h-6 rounded-full bg-blue-100 border-2 border-blue-300 mx-auto" title="Maintenance"></div>
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-100 mx-auto" title="Inactive Season"></div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="flex flex-wrap gap-4 mt-6 justify-center">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-200 border-2 border-green-500"></div>
            <span className="text-sm">Planting</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-amber-200 border-2 border-amber-500"></div>
            <span className="text-sm">Harvesting</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-purple-500"></div>
            <span className="text-sm">Planting & Harvesting</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-100 border-2 border-blue-300"></div>
            <span className="text-sm">Maintenance</span>
          </div>
        </div>
      </Card>
      
      {/* Farm Practices */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="h-full">
          <div className="flex items-center gap-3 mb-6">
            <Leaf className="h-6 w-6 text-green-600" />
            <h3 className="text-xl font-bold">Regenerative Practices</h3>
          </div>
          
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full mt-1">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium">Agroforestry Systems</h4>
                  <p className="text-gray-600 text-sm">
                    Coffee grown under a canopy of nitrogen-fixing and fruit trees, creating multiple income streams and enhancing biodiversity.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full mt-1">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium">Composting & Biochar</h4>
                  <p className="text-gray-600 text-sm">
                    All agricultural waste is processed into compost and biochar, improving soil health and sequestering carbon.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full mt-1">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium">Water Conservation</h4>
                  <p className="text-gray-600 text-sm">
                    Smart irrigation systems and water harvesting reduce water usage by 30% while maintaining optimal soil moisture.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full mt-1">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium">Natural Pest Management</h4>
                  <p className="text-gray-600 text-sm">
                    Biological pest control methods and companion planting eliminate the need for chemical pesticides.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="h-full">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="h-6 w-6 text-avn-purple-600" />
            <h3 className="text-xl font-bold">Technology Integration</h3>
          </div>
          
          <div className="space-y-4">
            <div className="bg-avn-purple-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="bg-avn-purple-100 p-2 rounded-full mt-1">
                  <Check className="h-5 w-5 text-avn-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium">IoT Soil Sensors</h4>
                  <p className="text-gray-600 text-sm">
                    Network of 45 sensors monitoring soil moisture, temperature, and nutrient levels in real-time across the farm.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-avn-purple-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="bg-avn-purple-100 p-2 rounded-full mt-1">
                  <Check className="h-5 w-5 text-avn-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium">AI-Driven Predictions</h4>
                  <p className="text-gray-600 text-sm">
                    Machine learning models predict optimal harvest times, pest outbreaks, and yield forecasts with 85% accuracy.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-avn-purple-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="bg-avn-purple-100 p-2 rounded-full mt-1">
                  <Check className="h-5 w-5 text-avn-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium">Blockchain Verification</h4>
                  <p className="text-gray-600 text-sm">
                    Every step from planting to processing is recorded on blockchain, creating an immutable record of ethical practices.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-avn-purple-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="bg-avn-purple-100 p-2 rounded-full mt-1">
                  <Check className="h-5 w-5 text-avn-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium">Mobile Farm Management</h4>
                  <p className="text-gray-600 text-sm">
                    All 273 farmers use the AVN mobile app to track activities, access market data, and receive personalized recommendations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
  
  const renderCommunityTab = () => (
    <div className="space-y-8">
      {/* Community Overview */}
      <Card className="bg-white p-6">
        <div className="flex items-center gap-3 mb-6">
          <Users className="h-6 w-6 text-avn-purple-600" />
          <h3 className="text-xl font-bold">Farmer Community</h3>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Total Farmers</div>
            <div className="text-2xl font-bold">273</div>
            <div className="text-xs text-gray-500">In cooperative</div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Women Farmers</div>
            <div className="text-2xl font-bold">142</div>
            <div className="text-xs text-gray-500">52% of total</div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Youth Farmers</div>
            <div className="text-2xl font-bold">87</div>
            <div className="text-xs text-gray-500">Under 35 years</div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Training Sessions</div>
            <div className="text-2xl font-bold">24</div>
            <div className="text-xs text-gray-500">Last quarter</div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-4">Farm Hub Leadership</h4>
            <div className="space-y-4">
              {farmHubMembers.map((member, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 rounded-full overflow-hidden">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{member.name}</div>
                    <div className="text-sm text-gray-600">{member.role}</div>
                  </div>
                  <div className="text-sm text-gray-500">{member.years} years</div>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-4">Community Activities</h4>
            <div className="space-y-3">
              <div className="bg-blue-50 border-l-4 border-blue-600 pl-3 py-3 pr-4">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-blue-800">Monthly Farmer Meeting</div>
                  <div className="text-sm text-blue-700">May 15</div>
                </div>
                <div className="text-sm text-blue-600 mt-1">Discussing irrigation improvements and new crop varieties</div>
              </div>
              
              <div className="bg-green-50 border-l-4 border-green-600 pl-3 py-3 pr-4">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-green-800">Agroforestry Workshop</div>
                  <div className="text-sm text-green-700">May 22</div>
                </div>
                <div className="text-sm text-green-600 mt-1">Training on companion planting and shade management</div>
              </div>
              
              <div className="bg-amber-50 border-l-4 border-amber-600 pl-3 py-3 pr-4">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-amber-800">Coffee Processing Training</div>
                  <div className="text-sm text-amber-700">June 5</div>
                </div>
                <div className="text-sm text-amber-600 mt-1">Advanced techniques for consistent coffee cherry processing</div>
              </div>
              
              <div className="bg-purple-50 border-l-4 border-purple-600 pl-3 py-3 pr-4">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-purple-800">Women Farmers Network</div>
                  <div className="text-sm text-purple-700">June 12</div>
                </div>
                <div className="text-sm text-purple-600 mt-1">Leadership and business skills development for women farmers</div>
              </div>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Community Feed */}
      <CommunityFeed />
      
      {/* Success Stories */}
      <Card className="bg-white p-6">
        <div className="flex items-center gap-3 mb-6">
          <Users className="h-6 w-6 text-avn-purple-600" />
          <h3 className="text-xl font-bold">Farmer Success Stories</h3>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <div className="h-48 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1605000797499-95a51c5269ae" 
                alt="Sarah Nakato" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h4 className="font-bold mb-1">Sarah Nakato</h4>
              <p className="text-gray-600 text-sm mb-3">
                "Since joining Edward's Hamlet cooperative, my coffee yield has increased by 40% and I now earn enough to send all three of my children to school."
              </p>
              <div className="text-avn-purple-600 text-sm font-medium flex items-center gap-1 hover:underline">
                <span>Read full story</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <div className="h-48 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1596627116790-af6f95d64f01" 
                alt="David Mugisha" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h4 className="font-bold mb-1">David Mugisha</h4>
              <p className="text-gray-600 text-sm mb-3">
                "The agroforestry training changed everything for me. Now I grow coffee, fruit, and timber on the same plot, and my soil gets better every year."
              </p>
              <div className="text-avn-purple-600 text-sm font-medium flex items-center gap-1 hover:underline">
                <span>Read full story</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <div className="h-48 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1604882356682-d9aaf9a50f2f" 
                alt="Esther Nabukeera" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h4 className="font-bold mb-1">Esther Nabukeera</h4>
              <p className="text-gray-600 text-sm mb-3">
                "I started processing coffee cherry syrup two years ago. Now I lead a group of 15 women creating value-added products that sell for premium prices."
              </p>
              <div className="text-avn-purple-600 text-sm font-medium flex items-center gap-1 hover:underline">
                <span>Read full story</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
  
  const renderMarketTab = () => (
    <div className="space-y-8">
      {/* Market Prices */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <MarketPrices />
        </div>
        
        <div className="md:col-span-2">
          <Card className="h-full">
            <div className="flex items-center gap-3 mb-6">
              <Coffee className="h-6 w-6 text-avn-purple-600" />
              <h3 className="text-xl font-bold">Coffee Export Analytics</h3>
            </div>
            
            <div className="grid grid-cols-3 gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Current Price</div>
                <div className="text-2xl font-bold">$3.45/lb</div>
                <div className="text-xs text-green-600">+0.22 (6.8%)</div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Quality Score</div>
                <div className="text-2xl font-bold">86.5</div>
                <div className="text-xs text-gray-500">Specialty Grade</div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Export Volume</div>
                <div className="text-2xl font-bold">18.5MT</div>
                <div className="text-xs text-green-600">+22% YoY</div>
              </div>
            </div>
            
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={[
                  {month: 'Jan', price: 3.15},
                  {month: 'Feb', price: 3.20},
                  {month: 'Mar', price: 3.18},
                  {month: 'Apr', price: 3.25},
                  {month: 'May', price: 3.32},
                  {month: 'Jun', price: 3.38},
                  {month: 'Jul', price: 3.45},
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" />
                  <YAxis domain={['auto', 'auto']} />
                  <Tooltip 
                    formatter={(value) => [`$${value}/lb`, 'Price']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="price" 
                    name="Coffee Price" 
                    stroke="#9333ea" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6, stroke: '#6d28d9' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>
      
      {/* Market Connections */}
      <Card className="bg-white p-6">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="h-6 w-6 text-avn-purple-600" />
          <h3 className="text-xl font-bold">Buyer Network</h3>
        </div>
        
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <div className="bg-avn-purple-100 p-3 rounded-full mx-auto mb-3 w-12 h-12 flex items-center justify-center">
              <Globe className="h-6 w-6 text-avn-purple-600" />
            </div>
            <div className="text-2xl font-bold">15</div>
            <div className="text-sm text-gray-600">Export Markets</div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <div className="bg-green-100 p-3 rounded-full mx-auto mb-3 w-12 h-12 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-green-600" />
            </div>
            <div className="text-2xl font-bold">28</div>
            <div className="text-sm text-gray-600">Direct Buyers</div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <div className="bg-blue-100 p-3 rounded-full mx-auto mb-3 w-12 h-12 flex items-center justify-center">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div className="text-2xl font-bold">35</div>
            <div className="text-sm text-gray-600">Active Orders</div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <div className="bg-amber-100 p-3 rounded-full mx-auto mb-3 w-12 h-12 flex items-center justify-center">
              <ShoppingBag className="h-6 w-6 text-amber-600" />
            </div>
            <div className="text-2xl font-bold">12</div>
            <div className="text-sm text-gray-600">Long-term Contracts</div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3 px-4 text-left">Buyer</th>
                <th className="py-3 px-4 text-left">Location</th>
                <th className="py-3 px-4 text-left">Product</th>
                <th className="py-3 px-4 text-left">Volume</th>
                <th className="py-3 px-4 text-left">Contract Value</th>
                <th className="py-3 px-4 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div className="font-medium">Ethical Bean Co.</div>
                  <div className="text-sm text-gray-500">Premium Roaster</div>
                </td>
                <td className="py-3 px-4">London, UK</td>
                <td className="py-3 px-4">Specialty Coffee</td>
                <td className="py-3 px-4">5.2 MT</td>
                <td className="py-3 px-4">$39,520</td>
                <td className="py-3 px-4">
                  <Badge variant="green">Active</Badge>
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div className="font-medium">Circular Foods</div>
                  <div className="text-sm text-gray-500">Sustainable Products</div>
                </td>
                <td className="py-3 px-4">Amsterdam, NL</td>
                <td className="py-3 px-4">Coffee Cherry Syrup</td>
                <td className="py-3 px-4">850 liters</td>
                <td className="py-3 px-4">$12,750</td>
                <td className="py-3 px-4">
                  <Badge variant="green">Active</Badge>
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div className="font-medium">GreenLife Markets</div>
                  <div className="text-sm text-gray-500">Retail Chain</div>
                </td>
                <td className="py-3 px-4">Toronto, CA</td>
                <td className="py-3 px-4">Coffee & Biochar</td>
                <td className="py-3 px-4">3.8 MT</td>
                <td className="py-3 px-4">$34,960</td>
                <td className="py-3 px-4">
                  <Badge variant="amber">Pending</Badge>
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div className="font-medium">Coffee Culture</div>
                  <div className="text-sm text-gray-500">Specialty Cafes</div>
                </td>
                <td className="py-3 px-4">Sydney, AU</td>
                <td className="py-3 px-4">Specialty Coffee</td>
                <td className="py-3 px-4">2.5 MT</td>
                <td className="py-3 px-4">$21,875</td>
                <td className="py-3 px-4">
                  <Badge variant="green">Active</Badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
      
      {/* Marketplace */}
      <CommunityMarketplace />
    </div>
  );
  
  const renderInnovationsTab = () => (
    <div className="space-y-8">
      <CoffeeByproducts />
      
      <Card className="bg-white p-6">
        <div className="flex items-center gap-3 mb-6">
          <Recycle className="h-6 w-6 text-green-600" />
          <h3 className="text-xl font-bold">Circular Innovation</h3>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
              <Recycle className="w-full h-full text-green-900" />
            </div>
            <h4 className="text-lg font-bold text-green-900 mb-3">Zero Waste Processing</h4>
            <p className="text-green-800 text-sm mb-4">
              100% of coffee cherry waste is converted into valuable products, from syrup to compost.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-800 text-sm">
                <Check className="h-4 w-4 text-green-700" />
                <span>4 value-added product streams</span>
              </div>
              <div className="flex items-center gap-2 text-green-800 text-sm">
                <Check className="h-4 w-4 text-green-700" />
                <span>12.5 tons of waste diverted annually</span>
              </div>
              <div className="flex items-center gap-2 text-green-800 text-sm">
                <Check className="h-4 w-4 text-green-700" />
                <span>$28,500 additional revenue</span>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
              <Droplets className="w-full h-full text-blue-900" />
            </div>
            <h4 className="text-lg font-bold text-blue-900 mb-3">Water Conservation</h4>
            <p className="text-blue-800 text-sm mb-4">
              Innovative water recycling and precision irrigation systems reduce water usage dramatically.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-blue-800 text-sm">
                <Check className="h-4 w-4 text-blue-700" />
                <span>30% reduction in water usage</span>
              </div>
              <div className="flex items-center gap-2 text-blue-800 text-sm">
                <Check className="h-4 w-4 text-blue-700" />
                <span>3.5 million liters saved annually</span>
              </div>
              <div className="flex items-center gap-2 text-blue-800 text-sm">
                <Check className="h-4 w-4 text-blue-700" />
                <span>Closed-loop processing water system</span>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
              <Sun className="w-full h-full text-amber-900" />
            </div>
            <h4 className="text-lg font-bold text-amber-900 mb-3">Solar Energy</h4>
            <p className="text-amber-800 text-sm mb-4">
              100% of processing operations powered by solar energy with battery storage system.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-amber-800 text-sm">
                <Check className="h-4 w-4 text-amber-700" />
                <span>45 kW solar installation</span>
              </div>
              <div className="flex items-center gap-2 text-amber-800 text-sm">
                <Check className="h-4 w-4 text-amber-700" />
                <span>60 kWh battery storage</span>
              </div>
              <div className="flex items-center gap-2 text-amber-800 text-sm">
                <Check className="h-4 w-4 text-amber-700" />
                <span>$12,500 annual energy savings</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
      
      <Card className="bg-white p-6">
        <div className="flex items-center gap-3 mb-6">
          <Zap className="h-6 w-6 text-avn-purple-600" />
          <h3 className="text-xl font-bold">Research & Development</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h4 className="font-medium mb-4">Current Research Projects</h4>
            <div className="space-y-3">
              <div className="bg-avn-purple-50 p-4 rounded-lg">
                <h5 className="font-medium mb-2">Climate-Resilient Coffee Varieties</h5>
                <p className="text-gray-700 text-sm mb-2">
                  Testing 5 drought and disease-resistant coffee varieties to identify optimal performers for changing climate conditions.
                </p>
                <div className="w-full bg-avn-purple-200 h-2 rounded-full overflow-hidden">
                  <div className="bg-avn-purple-600 h-full rounded-full" style={{ width: '65%' }}></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>In Progress</span>
                  <span>65%</span>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h5 className="font-medium mb-2">Biochar Carbon Sequestration</h5>
                <p className="text-gray-700 text-sm mb-2">
                  Measuring the long-term carbon sequestration potential of biochar application in coffee agroforestry systems.
                </p>
                <div className="w-full bg-green-200 h-2 rounded-full overflow-hidden">
                  <div className="bg-green-600 h-full rounded-full" style={{ width: '45%' }}></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>In Progress</span>
                  <span>45%</span>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="font-medium mb-2">Coffee Cherry Nutritional Analysis</h5>
                <p className="text-gray-700 text-sm mb-2">
                  Comprehensive nutritional and antioxidant analysis of coffee cherry byproducts for food and supplement applications.
                </p>
                <div className="w-full bg-blue-200 h-2 rounded-full overflow-hidden">
                  <div className="bg-blue-600 h-full rounded-full" style={{ width: '80%' }}></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>In Progress</span>
                  <span>80%</span>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-4">Innovation Metrics</h4>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">New Products</div>
                    <div className="text-2xl font-bold">4</div>
                    <div className="text-xs text-gray-500">Launched last year</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">R&D Investment</div>
                    <div className="text-2xl font-bold">$85K</div>
                    <div className="text-xs text-gray-500">Annual budget</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-2">Innovation ROI</div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-xl font-bold">285%</div>
                  <div className="text-sm text-green-600">+35% YoY</div>
                </div>
                <div className="text-xs text-gray-500 mb-2">Return on innovation investment</div>
                <div className="h-10">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      {year: '2022', roi: 180},
                      {year: '2023', roi: 210},
                      {year: '2024', roi: 250},
                      {year: '2025', roi: 285},
                    ]}>
                      <Bar dataKey="roi" fill="#9333ea" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-2">Research Partners</div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="purple">Makerere University</Badge>
                  <Badge variant="green">Jinja Agricultural Institute</Badge>
                  <Badge variant="blue">Coffee Research Institute</Badge>
                  <Badge variant="amber">CGIAR</Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="container mx-auto px-4 py-8">
        {/* Top Dashboard Header with Key Metrics */}
        <div className="bg-gradient-to-r from-avn-purple-900 to-avn-purple-800 rounded-xl shadow-xl text-white p-6 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-lg">
                <Building2 className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Edward's Hamlet</h1>
                <div className="flex items-center gap-2 text-white/80">
                  <MapPin className="h-4 w-4" />
                  <span>Jinja, Uganda</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="white" className="bg-white/20 text-white">
                <Users className="h-3 w-3 mr-1" />
                273 Farmers
              </Badge>
              <Badge variant="white" className="bg-white/20 text-white">
                <Leaf className="h-3 w-3 mr-1" />
                Regenerative Hub
              </Badge>
              <Badge variant="white" className="bg-white/20 text-white">
                <Zap className="h-3 w-3 mr-1" />
                Model Farm
              </Badge>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Coffee className="h-5 w-5" />
                <span className="text-white/90">Coffee Production</span>
              </div>
              <div className="text-2xl font-bold">18.5 MT</div>
              <div className="text-sm text-white/70">Annual yield</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5" />
                <span className="text-white/90">Yield Increase</span>
              </div>
              <div className="text-2xl font-bold">45%</div>
              <div className="text-sm text-white/70">Since AVN integration</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Recycle className="h-5 w-5" />
                <span className="text-white/90">Waste Recycled</span>
              </div>
              <div className="text-2xl font-bold">100%</div>
              <div className="text-sm text-white/70">Zero waste operation</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-5 w-5" />
                <span className="text-white/90">Farmer Revenue</span>
              </div>
              <div className="text-2xl font-bold">70%</div>
              <div className="text-sm text-white/70">Of final value (vs. 6% industry avg)</div>
            </div>
          </div>
        </div>
        
        {/* Dashboard Tabs */}
        <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'overview'
                ? 'text-avn-purple-600 border-avn-purple-600 font-medium'
                : 'text-gray-500 border-transparent hover:text-avn-purple-500 hover:border-avn-purple-300'
            }`}
          >
            <BarChart3 className="h-5 w-5" />
            <span>Farm Overview</span>
          </button>
          
          <button
            onClick={() => setActiveTab('farming')}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'farming'
                ? 'text-avn-purple-600 border-avn-purple-600 font-medium'
                : 'text-gray-500 border-transparent hover:text-avn-purple-500 hover:border-avn-purple-300'
            }`}
          >
            <Leaf className="h-5 w-5" />
            <span>Farming Practices</span>
          </button>
          
          <button
            onClick={() => setActiveTab('financial')}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'financial'
                ? 'text-avn-purple-600 border-avn-purple-600 font-medium'
                : 'text-gray-500 border-transparent hover:text-avn-purple-500 hover:border-avn-purple-300'
            }`}
          >
            <DollarSign className="h-5 w-5" />
            <span>Financial</span>
          </button>
          
          <button
            onClick={() => setActiveTab('community')}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'community'
                ? 'text-avn-purple-600 border-avn-purple-600 font-medium'
                : 'text-gray-500 border-transparent hover:text-avn-purple-500 hover:border-avn-purple-300'
            }`}
          >
            <Users className="h-5 w-5" />
            <span>Community</span>
          </button>
          
          <button
            onClick={() => setActiveTab('market')}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'market'
                ? 'text-avn-purple-600 border-avn-purple-600 font-medium'
                : 'text-gray-500 border-transparent hover:text-avn-purple-500 hover:border-avn-purple-300'
            }`}
          >
            <Globe className="h-5 w-5" />
            <span>Market Connections</span>
          </button>
          
          <button
            onClick={() => setActiveTab('innovations')}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'innovations'
                ? 'text-avn-purple-600 border-avn-purple-600 font-medium'
                : 'text-gray-500 border-transparent hover:text-avn-purple-500 hover:border-avn-purple-300'
            }`}
          >
            <Zap className="h-5 w-5" />
            <span>Innovations</span>
          </button>
        </div>
        
        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'farming' && renderFarmingTab()}
          {activeTab === 'financial' && renderFinancialTab()}
          {activeTab === 'community' && renderCommunityTab()}
          {activeTab === 'market' && renderMarketTab()}
          {activeTab === 'innovations' && renderInnovationsTab()}
        </div>
        
        {/* Edward's Hamlet Demo Access */}
        <div className="mt-8">
          <Card className="bg-gradient-to-r from-avn-purple-800 to-avn-purple-900 text-white">
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">Experience Edward's Farm Dashboard</h3>
                <p className="opacity-90 mb-4">
                  Access the interactive dashboard to explore Edward's farm management, real-time data, and analytics.
                </p>
                <Button 
                  variant="outline"
                  className="bg-white text-avn-purple-800 border-white hover:bg-white/90"
                  icon={<ArrowRight className="h-5 w-5" />}
                >
                  <a href="/dashboard">Access Demo Dashboard</a>
                </Button>
              </div>
              <div>
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="bg-white/10 backdrop-blur-sm p-6 rounded-full"
                >
                  <Building2 className="h-16 w-16 text-white" />
                </motion.div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EdwardsHamletDashboard;